import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { dmY } from "@/lib/dateFmt";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import EmptyState from "@/components/EmptyState";
import { Broom, ArrowLeft, ArrowClockwise, Trash, Warning } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);

/**
 * v1.21.0 — Retro-heal report for stranded commissions/portal fees.
 * These are commission or portal-fee rows whose linked payment has been
 * rejected, deleted, or moved to suspense — they inflate a party's
 * outstanding without a real payment behind them.
 */
export default function StrandedCommissions() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        setSelectedIds(new Set());
        try {
            const { data } = await api.get("/reports/stranded-commissions");
            setData(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const toggle = (id) => setSelectedIds((s) => {
        const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
    });
    const toggleAll = () => {
        const all = (data?.rows || []).map((r) => r.id);
        setSelectedIds((s) => (s.size === all.length ? new Set() : new Set(all)));
    };

    const heal = async () => {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Delete ${selectedIds.size} stranded commission/portal-fee row${selectedIds.size > 1 ? "s" : ""}?\n\nEach party's outstanding balance will drop accordingly. This is a permanent delete.`)) return;
        setBusy(true);
        try {
            const { data: r } = await api.post("/reports/stranded-commissions/heal", { ids: Array.from(selectedIds) });
            toast.success(`Deleted ${r.deleted} stranded row(s) — ledger healed`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const rows = data?.rows || [];

    return (
        <div className="space-y-6" data-testid="stranded-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <Link to="/admin" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-800">
                        <ArrowLeft size={12} /> Back to Dashboard
                    </Link>
                    <div className="mt-1 flex items-center gap-2">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-rose-500 to-red-600 text-white shadow-md">
                            <Broom size={20} weight="fill" />
                        </div>
                        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900">Stranded Commissions</h1>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">Commission / portal-fee entries whose linked payment was rejected, deleted, or moved to suspense — they inflate outstanding without a real payment behind them.</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={load} disabled={loading} data-testid="stranded-refresh"
                        className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm hover:bg-slate-100 disabled:opacity-50">
                        <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-4 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-rose-800">Stranded rows</div>
                    <div className="mt-2 font-display text-2xl font-bold text-rose-900" data-testid="stranded-count">{data?.count ?? "…"}</div>
                    <div className="text-[11px] text-rose-800/70">need healing</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Total inflating outstanding</div>
                    <div className="mt-2 font-display text-2xl font-bold text-slate-900" data-testid="stranded-total">{fmt(data?.total_amount)}</div>
                </div>
                <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-4 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-blue-800">Selected for cleanup</div>
                    <div className="mt-2 font-display text-2xl font-bold text-blue-900" data-testid="stranded-selected">{selectedIds.size}</div>
                    <div className="text-[11px] text-blue-800/70">rows chosen</div>
                </div>
            </div>

            {selectedIds.size > 0 && (
                <div className="flex items-center justify-between rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 shadow-sm" data-testid="stranded-action-bar">
                    <div className="flex items-center gap-2 text-sm text-rose-900">
                        <Warning size={16} weight="fill" /> {selectedIds.size} row{selectedIds.size > 1 ? "s" : ""} selected · deleting these will heal the corresponding party balances.
                    </div>
                    <div className="flex gap-2">
                        <button type="button" onClick={() => setSelectedIds(new Set())} className="text-xs font-medium text-slate-600 hover:text-slate-900 underline decoration-dotted">Clear</button>
                        <Button onClick={heal} disabled={busy} data-testid="stranded-heal-btn" className="bg-rose-600 text-white hover:bg-rose-700">
                            <Trash size={14} className="mr-1.5" /> {busy ? "Healing…" : `Delete ${selectedIds.size} row${selectedIds.size > 1 ? "s" : ""}`}
                        </Button>
                    </div>
                </div>
            )}

            <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                {rows.length === 0 && !loading ? (
                    <div className="p-6"><EmptyState icon={Broom} title="No stranded rows" description="Every commission and portal-fee is properly linked to an active payment. Nothing to heal." /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm" data-testid="stranded-table">
                            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="px-3 py-2 w-8">
                                        <input type="checkbox" data-testid="stranded-select-all"
                                            checked={selectedIds.size > 0 && selectedIds.size === rows.length}
                                            onChange={toggleAll}
                                            className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-rose-600" />
                                    </th>
                                    <th className="px-3 py-2 text-left">Date</th>
                                    <th className="px-3 py-2 text-left">Party</th>
                                    <th className="px-3 py-2 text-left">Type</th>
                                    <th className="px-3 py-2 text-right">Amount</th>
                                    <th className="px-3 py-2 text-left">Why stranded</th>
                                    <th className="px-3 py-2 text-left">Description</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {rows.map((r) => (
                                    <tr key={r.id} data-testid={`stranded-row-${r.id}`} className={selectedIds.has(r.id) ? "bg-rose-50/60" : "hover:bg-slate-50"}>
                                        <td className="px-3 py-2">
                                            <input type="checkbox" checked={selectedIds.has(r.id)} onChange={() => toggle(r.id)}
                                                data-testid={`stranded-check-${r.id}`}
                                                className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-rose-600" />
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-slate-700">{dmY(r.date)}</td>
                                        <td className="px-3 py-2">
                                            <button type="button" onClick={() => navigate(`/admin/reports?partyId=${r.party_id}`)} className="font-medium text-blue-700 hover:underline">
                                                {r.party_name}
                                            </button>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${r.txn_type === "commission" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{r.txn_type}</span>
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono font-semibold tabular-nums text-slate-900">{fmt(r.amount)}</td>
                                        <td className="px-3 py-2">
                                            <div className="flex flex-wrap gap-1">
                                                {(r.stranded_reasons || []).map((rz, i) => (
                                                    <span key={i} className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-800">{rz}</span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 max-w-[280px] truncate text-xs text-slate-600" title={r.description}>{r.description || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
